import { ComponentProps, useMemo, useRef, useState } from 'react';
import type { ValidatedPinUser } from 'washday-sdk';
import { getStoreByIdV2 } from '../../services/store.service';
import { useTranslation } from 'react-i18next';
import { MercadoPagoPaymentSheet } from '../../components/MercadoPagoPaymentSheet';
import { MercadoPagoEnum } from '../../enum/PaymentConfig';
import { IOrder } from '../../interfaces/Order';
import { IStore } from '../../interfaces/Store';
import { ICompany } from '../../interfaces/Company';
import { autoOpenCashDrawerForPayments } from '../../utils/cashDrawer';
import { createWebCompletionTicketAutoPrinter } from '../../utils/orderCompletionTicket';
import { showNotification } from '../../utils/Notification/Notification';
import { useRequestCashierPinValidation } from '../useRequestCashierPinValidation';
import { useFetchOrderBySequence } from './queries';
import { usePayAndCollect, useSetOrderCollected } from './mutations';

export interface CollectOrderParams {
  orderId?: string;
  sequence: string;
  storeId: string;
  collectWithAmountDue?: boolean;
  pinUserId?: string;
  pinValidationToken?: string;
  expectedStatus?: 'ready';
}
export interface PayAndCollectParams extends CollectOrderParams {
  paymentMethod: string;
  cashierBoxId: string;
  amount: number;
  paymentDate: string;
  collectWithAmountDue: boolean;
  attemptId?: string | null;
}
export interface MercadoPagoCollectionSelection {
  order: IOrder;
  storeId: string;
  collectWithAmountDue: boolean;
  pinUserId?: string;
  pinValidationToken?: string;
  expectedStatus?: 'ready';
  cashierBoxId?: string;
  amount?: number;
}
type ApprovedPayment = {
  attemptId?: string;
  mpAttempt?: { attemptId?: string; attempt?: { _id?: string } };
};

// These are the existing Ready/List submissions, shared with the work board.
export function useOrderCollectionActions({
  store,
  company
}: {
  store?: IStore;
  company?: Pick<ICompany, 'logoUrl' | 'companyTaxInfo'>;
}) {
  const { t } = useTranslation(['Notifications']);
  const setOrderCollected = useSetOrderCollected();
  const payAndCollect = usePayAndCollect();
  const { requestPin } = useRequestCashierPinValidation();
  const fetchOrderBySequence = useFetchOrderBySequence();
  const submitting = useRef(false);
  const [mpContext, setMpContext] = useState<MercadoPagoCollectionSelection | null>(null);
  const mpContextRef = useRef(mpContext);
  const approvedAttempt = useRef<string | null>(null);
  const approvalInFlight = useRef(false);
  const completionTicketAutoPrint = useMemo(
    () =>
      createWebCompletionTicketAutoPrinter({
        fetchOrderBySequence,
        t,
        companyLogoUrl: company?.logoUrl,
        companyTaxInfo: company?.companyTaxInfo || undefined
      }),
    [fetchOrderBySequence, t, company?.logoUrl, company?.companyTaxInfo]
  );

  const onAskForPin = async (options?: Parameters<typeof requestPin>[0]) => {
    const targetStoreId = options?.storeId || store?._id;
    const targetStore = targetStoreId && targetStoreId !== store?._id
      ? await getStoreByIdV2(targetStoreId) as unknown as IStore
      : store;
    if (!targetStore?._id || targetStore._id !== targetStoreId) throw new Error(t('Notifications:somethingWrong'));
    if (targetStore.requireCashierPinToCollect) {
      const user = await requestPin({ ...options, purpose: 'order-collection', storeId: targetStoreId });
      if (user?._id) return user;
      throw new Error(t('Notifications:pinRequired'));
    }
    return null;
  };
  const onOrderCollected = async ({
    orderId,
    sequence,
    storeId,
    collectWithAmountDue = false,
    pinUserId,
    pinValidationToken,
    expectedStatus
  }: CollectOrderParams) => {
    if (submitting.current || setOrderCollected.isPending || payAndCollect.isPending) return false;
    submitting.current = true;
    try {
      await setOrderCollected.mutateAsync({
        sequence,
        storeId: storeId || '',
        data: {
          collectedDateTime: new Date().toISOString(),
          collectWithAmountDue,
          orderId: orderId || undefined,
          pinUserId: pinUserId || undefined,
          ...(pinValidationToken ? { pinValidationToken } : {}),
          ...(expectedStatus ? { expectedStatus } : {})
        }
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      void completionTicketAutoPrint(
        orderId || { sequence, storeId },
        store?.orderPageConfig?.printTicketAfterOrderCompletion
      );
      return true;
    } catch (error) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw error;
    } finally {
      submitting.current = false;
    }
  };
  const onOrderPayAndCollect = async (params: PayAndCollectParams) => {
    if (submitting.current || payAndCollect.isPending || setOrderCollected.isPending) return false;
    submitting.current = true;
    const {
      orderId,
      sequence,
      storeId,
      paymentMethod,
      cashierBoxId,
      amount,
      paymentDate,
      collectWithAmountDue,
      pinUserId,
      pinValidationToken,
      attemptId,
      expectedStatus
    } = params;
    try {
      await payAndCollect.mutateAsync({
        orderId: orderId || undefined,
        sequence,
        storeId: storeId || '',
        paymentMethod,
        cashierBoxId,
        amount,
        paymentDate,
        collectedDateTime: new Date().toISOString(),
        collectWithAmountDue,
        pinUserId: pinUserId || undefined,
        ...(pinValidationToken ? { pinValidationToken } : {}),
        attemptId: attemptId || undefined,
        ...(expectedStatus ? { expectedStatus } : {})
      });
      showNotification({ type: 'Default', title: t('Notifications:success') });
      void autoOpenCashDrawerForPayments({
        cashDrawerConfig: store?.cashDrawerConfig,
        payments: [{ paymentMethod, amountPaid: amount }],
        source: 'payment'
      }).then((result) => {
        if (result.attempted && !result.success)
          showNotification({
            type: 'Error',
            title: t('Notifications:cashDrawerOpenFailedTitle'),
            message: result.errorMessage || t('Notifications:cashDrawerOpenFailedBody')
          });
      });
      if (orderId)
        void completionTicketAutoPrint(
          orderId,
          store?.orderPageConfig?.printTicketAfterOrderCompletion
        );
      return true;
    } catch (error) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      throw error;
    } finally {
      submitting.current = false;
    }
  };
  const closeMercadoPago = () => {
    mpContextRef.current = null;
    setMpContext(null);
  };
  const onMercadoPagoSelected = (selection: MercadoPagoCollectionSelection) => {
    approvedAttempt.current = null;
    mpContextRef.current = selection;
    setMpContext(selection);
  };
  const onMpApproved = async (
    approved?: ApprovedPayment,
    renewPin?: () => Promise<ValidatedPinUser | null>
  ) => {
    const context = mpContextRef.current;
    const attemptId = approved?.attemptId || approved?.mpAttempt?.attemptId || approved?.mpAttempt?.attempt?._id;
    if (!context?.order?.sequence || !attemptId) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
      return false;
    }
    if (approvedAttempt.current === attemptId || submitting.current || approvalInFlight.current) return false;
    approvalInFlight.current = true;
    let collectionContext = context;
    const submitApprovedCollection = () => onOrderPayAndCollect({
      orderId: collectionContext.order._id || '', sequence: String(collectionContext.order.sequence),
      storeId: collectionContext.storeId, paymentMethod: MercadoPagoEnum.MERCADOPAGO,
      cashierBoxId: collectionContext.cashierBoxId || '', amount: collectionContext.amount || 0,
      paymentDate: new Date().toISOString(), collectWithAmountDue: collectionContext.collectWithAmountDue || false,
      pinUserId: collectionContext.pinUserId, pinValidationToken: collectionContext.pinValidationToken,
      expectedStatus: collectionContext.expectedStatus, attemptId
    });
    try {
      let completed: boolean;
      try {
        completed = await submitApprovedCollection();
      } catch (error) {
        const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
        if (!['PIN_REQUIRED', 'PIN_VALIDATION_INVALID', 'PIN_VALIDATION_EXPIRED'].includes(code || '') || mpContextRef.current !== context) return false;
        // The payment is already approved. Renew PIN and retry this same collection, never a charge.
        const user = await (renewPin ? renewPin() : requestPin({ purpose: 'order-collection', storeId: context.storeId }));
        if (!user?._id || mpContextRef.current !== context) return false;
        collectionContext = { ...context, pinUserId: user._id, pinValidationToken: user.pinValidationToken };
        mpContextRef.current = collectionContext;
        setMpContext(collectionContext);
        completed = await submitApprovedCollection();
      }
      if (!completed) return false;
      approvedAttempt.current = attemptId;
      if (mpContextRef.current === collectionContext) closeMercadoPago();
      return true;
    } catch {
      return false;
    } finally {
      approvalInFlight.current = false;
    }
  };
  const order = mpContext?.order;
  const mercadoPagoSheetProps: ComponentProps<typeof MercadoPagoPaymentSheet> = {
    opened: !!mpContext,
    onClose: closeMercadoPago,
    onApproved: async (approved) => {
      await onMpApproved(approved);
    },
    order: order
      ? {
          ...order,
          folioPrefix: order.folioPrefix || '',
          sequence: order.sequence || '',
          _id: order._id || '',
          cashierBoxId: mpContext.cashierBoxId || '',
          total: order.total || 0,
          amountPaid: order.amountPaid || 0,
          currency: 'MXN',
          paymentLines: order.paymentLines || [],
          createdIn: {
            _id:
              typeof order.createdIn === 'object'
                ? order.createdIn?._id
                : order.createdIn || mpContext.storeId,
            currencySymbol:
              typeof order.createdIn === 'object' ? order.createdIn?.currencySymbol : undefined
          }
        }
      : null,
    storeId: mpContext?.storeId || store?._id,
    autoCloseOnApproved: false,
    print_on_terminal: store?.mpSettings?.print_on_terminal
  };
  return {
    onAskForPin,
    onOrderCollected,
    onOrderPayAndCollect,
    onMercadoPagoSelected,
    onMpApproved,
    closeMercadoPago,
    mercadoPagoSheetProps,
    isCollecting: setOrderCollected.isPending,
    isPaying: payAndCollect.isPending
  };
}
